'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import {
  MapPin,
  Clock,
  SlidersHorizontal,
  ChevronDown,
  Search,
  Truck,
  X,
  ShoppingBag,
  LocateFixed,
  Home,
  Briefcase,
  Plus,
  Shield,
  Eye,
  Flame,
  Star,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { milesBetween } from '@/lib/location/distance';
import { reverseGeocode } from '@/lib/location/geocode';

type SortOption = 'distance' | 'rating' | 'delivery' | 'fee';
type LocationSource = 'browser' | 'saved-profile' | 'manual' | 'none';

interface Vendor {
  id: string;
  name: string;
  cuisine: string;
  category: string;
  businessType?: 'personal_chef' | 'food_truck' | null;
  image: string;
  imageAlt: string;
  avatar: string;
  bio?: string | null;
  business_hours?: string | null;
  availability_override?: 'open' | 'closed' | null;
  rating: number;
  reviewCount: number;
  distance: number;
  deliveryTime: string;
  deliveryFee: number;
  priceRange: '$' | '$$' | '$$$';
  isOpen: boolean;
  openLabel: string;
  knownFor: string;
  locationLabel: string;
  serviceRadiusMiles: number;
  trustScore: number;
  trustLabel: string;
  email_verified?: boolean | null;
  phone_verified?: boolean | null;
  identity_verified?: boolean | null;
  completed_orders?: number | null;
  complaints_count?: number | null;
  badges: Array<'verified_identity' | 'certified' | 'licensed_business' | 'top_rated' | 'pro_chef' | 'new_chef'>;
  approvedCredentialsCount: number;
}

interface DbVendorRow {
  id: string;
  full_name: string;
  bio: string | null;
  location: string | null;
  avatar_url: string | null;
  latitude: number | null;
  longitude: number | null;
  service_radius_miles: number | null;
  delivery_enabled?: boolean | null;
  delivery_fee?: number | null;
  business_hours?: string | null;
  availability_override?: 'open' | 'closed' | null;
  trust_score?: number | null;
  trust_label?: string | null;
  approved_credentials_count?: number | null;
  email_verified?: boolean | null;
  phone_verified?: boolean | null;
  identity_verified?: boolean | null;
  completed_orders?: number | null;
  complaints_count?: number | null;
  rating_avg?: number | null;
  rating_count?: number | null;
  is_verified?: boolean | null;
  is_certified?: boolean | null;
  is_licensed?: boolean | null;
  is_top_rated?: boolean | null;
  is_pro_chef?: boolean | null;
  business_type?: 'personal_chef' | 'food_truck' | null;
}

interface SavedAddressOption {
  id: string;
  label: string;
  value: string;
  icon: 'home' | 'work';
}

interface DisplayLocation {
  shortLabel: string;
  fullAddress: string;
}

const CATEGORIES = [
  { id: 'all', label: 'All', emoji: '🍽️' },
  { id: 'chef', label: 'Chefs', emoji: '👨‍🍳' },
  { id: 'food_truck', label: 'Food Trucks', emoji: '🚚' },
];

const SORT_OPTIONS: { id: SortOption; label: string; icon: string }[] = [
  { id: 'distance', label: 'Nearest', icon: '📍' },
  { id: 'rating', label: 'Top Rated', icon: '⭐' },
  { id: 'delivery', label: 'Fastest', icon: '⚡' },
  { id: 'fee', label: 'Lowest Fee', icon: '💰' },
];

const CUSTOMER_RADIUS_OPTIONS = [5, 10, 15, 25];
const DEFAULT_LOCATION = 'Set your location';
const VENDOR_AVATAR_FALLBACK = '/assets/images/no_image.png';
const QUICK_FILTERS = ['Open now', 'Nearest', 'Top rated', 'Soul food', 'Healthy', 'Seafood', 'BBQ', 'Vegan'];

function hasRealVendorImage(image?: string | null) {
  if (!image) return false;
  return image !== VENDOR_AVATAR_FALLBACK;
}

function VendorImageFallback({ vendor, compact = false }: { vendor: Vendor; compact?: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-[#2B1608] via-[#5B2C0B] to-[#C2410C]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.18),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(251,146,60,0.22),transparent_38%)]" />
      <div className="absolute right-3 top-3 rounded-full border border-white/15 bg-white/10 p-2 text-white/80 backdrop-blur-sm">
        <ShoppingBag className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
      </div>
      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
        <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-white/80`}>{vendor.businessType === 'food_truck' ? 'Food Truck' : 'Personal Chef'}</p>
        <p className={`${compact ? 'text-lg' : 'text-2xl'} font-700 leading-tight tracking-snug line-clamp-2`}>{vendor.name}</p>
        <p className={`${compact ? 'text-[11px]' : 'text-xs'} mt-1 text-white/80 line-clamp-1`}>{vendor.knownFor}</p>
      </div>
    </div>
  );
}

function PriceRange({ range }: { range: '$' | '$$' | '$$$' }) {
  return (
    <span className="text-xs text-muted-foreground font-500">
      {['$', '$$', '$$$'].map((p, i) => (
        <span key={i} className={p.length <= range.length ? 'text-foreground' : 'opacity-30'}>$</span>
      ))}
    </span>
  );
}

function formatDistance(distance: number) {
  if (distance < 1) return '< 1 mi';
  return `${distance.toFixed(1)} mi`;
}

function inferKnownFor(vendor: DbVendorRow) {
  const bio = vendor.bio?.trim();
  if (!bio) return 'Chef specials';
  const firstSentence = bio.split(/[.!?]/)[0]?.trim();
  if (!firstSentence) return 'Chef specials';
  return firstSentence.length > 44 ? `${firstSentence.slice(0, 41)}...` : firstSentence;
}

function shortAddress(label: string) {
  const value = label?.trim() || DEFAULT_LOCATION;
  if (value === DEFAULT_LOCATION) return value;
  return value.split(',')[0]?.trim() || value;
}

function getVendorOpenState(hoursText?: string | null, availabilityOverride?: 'open' | 'closed' | null) {
  if (availabilityOverride === 'open') return { label: 'Open now', isOpen: true };
  if (availabilityOverride === 'closed') return { label: 'Closed manually', isOpen: false };
  if (!hoursText || hoursText.toLowerCase().includes('closed all week')) return { label: 'Closed now', isOpen: false };

  const [daysPart = '', timePart = ''] = hoursText.split('•').map((part) => part.trim());
  const today = new Date().toLocaleDateString('en-US', { weekday: 'short' });
  const openDays = daysPart.split(',').map((part) => part.trim()).filter(Boolean);

  if (!openDays.includes(today)) return { label: 'Closed today', isOpen: false };

  const timeMatch = timePart.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
  if (!timeMatch) return { label: 'Open today', isOpen: true };

  const toMinutes = (value: string) => {
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = toMinutes(timeMatch[1]);
  const closeMinutes = toMinutes(timeMatch[2]);

  if (nowMinutes >= openMinutes && nowMinutes < closeMinutes) return { label: 'Open now', isOpen: true };
  if (nowMinutes < openMinutes) return { label: `Opens at ${timeMatch[1]}`, isOpen: false };
  return { label: 'Closed now', isOpen: false };
}

function FeaturedVendorCard({ vendor }: { vendor: Vendor }) {
  const featuredBadge = vendor.badges.includes('top_rated') ? 'Top Rated' : vendor.badges.includes('verified_identity') ? 'Verified' : 'Popular';
  const hasImage = hasRealVendorImage(vendor.image);

  return (
    <Link href={`/vendor-profile?id=${vendor.id}`} className="block min-w-[280px] max-w-[280px] shrink-0">
      <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-subtle hover:shadow-card-hover transition-all">
        <div className="relative h-36 bg-muted overflow-hidden">
          {hasImage ? <img src={vendor.image} alt={vendor.imageAlt} className="w-full h-full object-cover" /> : <VendorImageFallback vendor={vendor} compact />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-700 text-white backdrop-blur-sm">
            <Flame className="w-3 h-3 text-orange-300" />
            {featuredBadge}
          </div>
          <div className="absolute bottom-3 left-3 right-3 flex items-end gap-3">
            <img src={vendor.avatar} alt={`${vendor.name} avatar`} className="w-11 h-11 rounded-full border-2 border-white/80 object-cover shrink-0" />
            <div className="min-w-0 text-white">
              <p className="text-sm font-700 truncate">{vendor.name}</p>
              <p className="text-[11px] text-white/80 truncate">{vendor.cuisine}</p>
            </div>
          </div>
        </div>
        <div className="p-3 space-y-2.5">
          <p className="text-[12px] text-muted-foreground line-clamp-2">{vendor.knownFor}</p>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1"><MapPin className="w-3 h-3 text-primary" />{formatDistance(vendor.distance)}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1"><Clock className="w-3 h-3" />{vendor.deliveryTime}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1"><Star className="w-3 h-3 text-primary" />{vendor.rating > 0 ? vendor.rating.toFixed(1) : 'New'}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1"><PriceRange range={vendor.priceRange} /></span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function VendorCard({ vendor }: { vendor: Vendor }) {
  const hasImage = hasRealVendorImage(vendor.image);

  return (
    <div className="bg-card rounded-2xl overflow-hidden border border-border/50 hover:border-primary/25 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-250 active:scale-[0.99] group shadow-subtle">
      <Link href={`/vendor-profile?id=${vendor.id}`} className="block">
        <div className="relative h-44 overflow-hidden bg-muted">
          {hasImage ? <img src={vendor.image} alt={vendor.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400" /> : <VendorImageFallback vendor={vendor} />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
          <div className="absolute top-3 right-3 bg-black/45 backdrop-blur-sm text-white text-[11px] font-700 px-2.5 py-1 rounded-full shrink-0">
            {formatDistance(vendor.distance)}
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-white font-700 text-base leading-tight truncate">{vendor.name}</p>
            <p className="text-white/85 text-xs truncate mt-0.5">{vendor.cuisine}</p>
          </div>
        </div>
      </Link>

      <div className="px-4 pt-3.5 pb-4">
        <div className="flex items-center gap-2 mb-2.5 min-w-0">
          <img src={vendor.avatar} alt={`${vendor.name} vendor avatar`} className="w-8 h-8 rounded-full object-cover border border-border/50 shrink-0" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-[13px] font-700 text-foreground truncate">{vendor.name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{vendor.locationLabel}</p>
          </div>
          <PriceRange range={vendor.priceRange} />
        </div>

        <div className="flex items-center gap-2 text-[12px] text-muted-foreground mb-3 flex-wrap">
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            <span>{formatDistance(vendor.distance)}</span>
          </span>
          <span className="opacity-40">•</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{vendor.deliveryTime}</span>
          </span>
        </div>

        <div className="rounded-xl bg-muted/60 px-3 py-2.5 mb-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {[vendor.cuisine, vendor.knownFor].filter(Boolean).slice(0, 2).map((tag) => (
              <span key={tag} className="inline-flex max-w-full truncate rounded-full bg-background/80 px-2 py-1 text-[10px] font-600 text-muted-foreground border border-border/50">{tag}</span>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-700 mb-1">Known for</p>
              <p className="text-[13px] text-foreground font-600 leading-snug">{vendor.knownFor}</p>
            </div>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${vendor.isOpen ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
              {vendor.openLabel}
            </span>
          </div>
        </div>

        <Link href={`/vendor-profile?id=${vendor.id}`} className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-[13px] font-700 transition-all duration-200 tracking-snug bg-primary text-white hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20 active:scale-[0.98] shadow-sm shadow-primary/15">
          <ShoppingBag className="w-4 h-4" />
          View Chef
        </Link>
      </div>
    </div>
  );
}

export default function NearbyPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState<SortOption>('distance');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showLocationSheet, setShowLocationSheet] = useState(false);
  const [showOpenOnly, setShowOpenOnly] = useState(false);
  const [customerRadiusMiles, setCustomerRadiusMiles] = useState<number>(10);
  const [customInput, setCustomInput] = useState('');
  const [manualLocationLabel, setManualLocationLabel] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [displayLocation, setDisplayLocation] = useState<DisplayLocation>({ shortLabel: DEFAULT_LOCATION, fullAddress: DEFAULT_LOCATION });
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationCoords, setLocationCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationSource, setLocationSource] = useState<LocationSource>('none');
  const [locationError, setLocationError] = useState('');
  const [locationLoading, setLocationLoading] = useState(true);
  const lastSavedLocationRef = useRef<{ latitude: number; longitude: number; fullAddress: string } | null>(null);

  const { user, profile } = useAuth();
  const supabase = createClient();

  const savedAddresses = useMemo<SavedAddressOption[]>(() => {
    const options: SavedAddressOption[] = [];
    if (profile?.location) {
      options.push({ id: 'home', label: 'Home', value: profile.location, icon: 'home' });
    }
    if (manualLocationLabel && manualLocationLabel !== profile?.location) {
      options.push({ id: 'work', label: 'Work', value: manualLocationLabel, icon: 'work' });
    }
    return options;
  }, [profile?.location, manualLocationLabel]);

  useEffect(() => {
    if (profile?.location && locationSource === 'none') {
      setDisplayLocation({ shortLabel: shortAddress(profile.location), fullAddress: profile.location });
    }
    if (typeof profile?.latitude === 'number' && typeof profile?.longitude === 'number' && locationSource === 'none') {
      setLocationCoords({ latitude: profile.latitude, longitude: profile.longitude });
      setLocationSource('saved-profile');
      const full = profile.location || 'Saved location';
      setDisplayLocation({ shortLabel: shortAddress(full), fullAddress: full });
    }
  }, [profile, locationSource]);

  useEffect(() => {
    requestBrowserLocation();
  }, [user?.id, profile?.id]);

  useEffect(() => {
    loadVendors();
  }, [profile?.id, locationCoords?.latitude, locationCoords?.longitude, customerRadiusMiles, manualLocationLabel]);

  const persistLiveLocation = async (nextCoords: { latitude: number; longitude: number }, fullAddress: string) => {
    if (!user?.id) return;

    const last = lastSavedLocationRef.current;
    const locationChangedEnough = !last || milesBetween(last.latitude, last.longitude, nextCoords.latitude, nextCoords.longitude) > 0.03 || last.fullAddress !== fullAddress;
    if (!locationChangedEnough) return;

    lastSavedLocationRef.current = { ...nextCoords, fullAddress };

    try {
      await supabase.from('user_profiles').update({
        location: fullAddress,
        latitude: nextCoords.latitude,
        longitude: nextCoords.longitude,
        updated_at: new Date().toISOString(),
      }).eq('id', user.id);
    } catch {}
  };

  const handleBrowserLocationSuccess = async (position: GeolocationPosition) => {
    const nextCoords = { latitude: position.coords.latitude, longitude: position.coords.longitude };
    setUserLocation(nextCoords);
    setLocationCoords(nextCoords);
    setLocationSource('browser');
    setLocationError('');
    setLocationLoading(false);

    const resolved = await reverseGeocode(nextCoords.latitude, nextCoords.longitude);
    const fullAddress = resolved?.fullAddress || profile?.location || 'Current location';
    const shortLabel = resolved?.shortLabel || shortAddress(fullAddress);
    setDisplayLocation({ shortLabel, fullAddress });
    await persistLiveLocation(nextCoords, fullAddress);
  };

  const handleBrowserLocationFailure = () => {
    setLocationLoading(false);
    if (typeof profile?.latitude === 'number' && typeof profile?.longitude === 'number') {
      const savedCoords = { latitude: profile.latitude, longitude: profile.longitude };
      setUserLocation(savedCoords);
      setLocationCoords(savedCoords);
      setLocationSource('saved-profile');
      const full = profile.location || 'Saved location';
      setDisplayLocation({ shortLabel: shortAddress(full), fullAddress: full });
      setLocationError('Location permission denied. Using your saved profile location.');
    } else if (manualLocationLabel.trim()) {
      setUserLocation(null);
      setLocationSource('manual');
      setDisplayLocation({ shortLabel: shortAddress(manualLocationLabel.trim()), fullAddress: manualLocationLabel.trim() });
      setLocationCoords(null);
      setLocationError('Location permission denied. Using your manual location fallback.');
    } else {
      setUserLocation(null);
      setLocationSource('none');
      setLocationCoords(null);
      const full = profile?.location || DEFAULT_LOCATION;
      setDisplayLocation({ shortLabel: shortAddress(full), fullAddress: full });
      setLocationError('Location permission denied. Add or save a location to see nearby chefs accurately.');
    }
  };

  const requestSingleLocation = () => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      setLocationLoading(false);
      setLocationError('Browser location is unavailable. Using saved profile location if available.');
      return;
    }

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      handleBrowserLocationSuccess,
      handleBrowserLocationFailure,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  const loadVendors = async () => {
    setLoadingVendors(true);
    try {
      const response = await fetch('/api/nearby-chefs', { cache: 'no-store' });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || 'Failed to load nearby chefs.');

      const hasCoords = typeof locationCoords?.latitude === 'number' && typeof locationCoords?.longitude === 'number';
      const normalizedManual = manualLocationLabel.trim().toLowerCase();

      const rawRows = ((payload?.chefs as DbVendorRow[] | null) ?? []);

      const mapped: Vendor[] = rawRows
        .map((row) => {
          if (typeof row.latitude !== 'number' || typeof row.longitude !== 'number') return null;
          const chefServiceRadius = row.service_radius_miles || 10;
          const distance = hasCoords ? milesBetween(locationCoords!.latitude, locationCoords!.longitude, row.latitude, row.longitude) : null;
          const insideCustomerRadius = distance == null ? false : distance <= customerRadiusMiles;

          if (hasCoords && !insideCustomerRadius) return null;
          if (!hasCoords) {
            if (!normalizedManual) return null;
            const rowLocation = (row.location || '').toLowerCase();
            if (!rowLocation.includes(normalizedManual)) return null;
          }

          const badges = [
            row.is_verified ? 'verified_identity' : null,
            row.is_certified ? 'certified' : null,
            row.is_licensed ? 'licensed_business' : null,
            row.is_top_rated ? 'top_rated' : null,
            row.is_pro_chef ? 'pro_chef' : null,
          ].filter(Boolean) as Vendor['badges'];
          const openState = getVendorOpenState(row.business_hours || null, row.availability_override || null);

          return {
            id: row.id,
            name: row.full_name || 'Chef',
            cuisine: row.business_type === 'food_truck' ? 'Food Truck' : (row.bio?.split('.')[0] || 'Personal Chef'),
            category: row.business_type === 'food_truck' ? 'food_truck' : 'chef',
            image: row.avatar_url || VENDOR_AVATAR_FALLBACK,
            imageAlt: `${row.full_name || 'Chef'} profile preview`,
            avatar: row.avatar_url || VENDOR_AVATAR_FALLBACK,
            bio: row.bio,
            business_hours: row.business_hours || null,
            availability_override: row.availability_override || null,
            rating: Number(row.trust_score ? row.rating_avg || 0 : row.rating_avg || 0),
            reviewCount: Number(row.rating_count || 0),
            distance: distance == null ? 0 : Number(distance.toFixed(1)),
            deliveryTime: '25–35 min',
            deliveryFee: Number(row.delivery_fee || 0),
            businessType: row.business_type || 'personal_chef',
            priceRange: '$$',
            isOpen: openState.isOpen,
            openLabel: openState.label,
            knownFor: inferKnownFor(row),
            locationLabel: row.location || 'Location unavailable',
            serviceRadiusMiles: chefServiceRadius,
            trustScore: row.trust_score || 0,
            trustLabel: row.trust_label || 'Low trust',
            email_verified: row.email_verified || false,
            phone_verified: row.phone_verified || false,
            identity_verified: row.identity_verified || false,
            completed_orders: row.completed_orders || 0,
            complaints_count: row.complaints_count || 0,
            badges,
            approvedCredentialsCount: row.approved_credentials_count || 0,
          };
        })
        .filter((row): row is Vendor => Boolean(row));

      setVendors(mapped);
    } catch {
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };

  const requestBrowserLocation = () => {
    setShowLocationSheet(false);
    requestSingleLocation();
  };

  const handleLocationChange = async (loc: string) => {
    const nextLabel = loc.trim();
    setManualLocationLabel(nextLabel);
    setDisplayLocation({ shortLabel: shortAddress(nextLabel || DEFAULT_LOCATION), fullAddress: nextLabel || DEFAULT_LOCATION });
    setLocationSource(nextLabel ? 'manual' : 'none');
    setLocationCoords(null);
    setShowLocationSheet(false);
    setCustomInput('');

    if (user?.id && nextLabel) {
      try {
        await supabase.from('user_profiles').update({ location: nextLabel, updated_at: new Date().toISOString() }).eq('id', user.id);
      } catch {}
    }
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customInput.trim()) handleLocationChange(customInput.trim());
  };

  const filteredVendors = useMemo(() => {
    return vendors
      .filter((v) => {
        const matchesCategory = selectedCategory === 'all' || v.category === selectedCategory;
        const matchesSearch = !searchQuery || v.name.toLowerCase().includes(searchQuery.toLowerCase()) || v.cuisine.toLowerCase().includes(searchQuery.toLowerCase()) || v.knownFor.toLowerCase().includes(searchQuery.toLowerCase()) || v.locationLabel.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesOpen = !showOpenOnly || v.isOpen;
        return matchesCategory && matchesSearch && matchesOpen;
      })
      .sort((a, b) => {
        if (sortBy === 'distance') return a.distance - b.distance;
        if (sortBy === 'rating') return b.rating - a.rating;
        if (sortBy === 'delivery') return a.deliveryTime.localeCompare(b.deliveryTime);
        if (sortBy === 'fee') return a.deliveryFee - b.deliveryFee;
        return 0;
      });
  }, [vendors, selectedCategory, searchQuery, showOpenOnly, sortBy]);

  const activeSortLabel = SORT_OPTIONS.find((s) => s.id === sortBy);

  const headerLocation = (
    <button onClick={() => setShowLocationSheet(true)} className="max-w-[170px] sm:max-w-[260px] flex items-center gap-1.5 text-[13px] font-700 text-foreground hover:text-primary transition-colors min-w-0">
      <span className="truncate">{displayLocation.shortLabel}</span>
      <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    </button>
  );

  return (
    <AppLayout headerCenter={headerLocation}>
      <div className="max-w-screen-lg mx-auto px-4 py-4">
        <div className="flex items-center justify-between py-1 gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            <span className="font-700 text-foreground">{filteredVendors.length}</span> food businesses nearby
          </p>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <button
              onClick={() => setShowOpenOnly(!showOpenOnly)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 border transition-all ${showOpenOnly ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400' : 'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${showOpenOnly ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
              Open now
            </button>

            <div className="relative">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 border transition-all ${showSortMenu ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'}`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                <span>{activeSortLabel?.icon}</span>
                {activeSortLabel?.label}
                <ChevronDown className={`w-3 h-3 transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
              </button>
              {showSortMenu && (
                <div className="absolute right-0 top-full mt-1.5 bg-card border border-border rounded-xl shadow-xl z-40 overflow-hidden min-w-[170px]">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-[10px] font-700 text-muted-foreground uppercase tracking-wider">Sort by</p>
                  </div>
                  {SORT_OPTIONS.map((opt) => (
                    <button key={opt.id} onClick={() => { setSortBy(opt.id); setShowSortMenu(false); }} className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2.5 ${sortBy === opt.id ? 'bg-primary/10 text-primary font-700' : 'text-foreground hover:bg-muted'}`}>
                      <span>{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {locationLoading && <p className="text-xs text-muted-foreground mt-2 mb-3">Finding chefs near you...</p>}
        {locationError && <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 mb-3">{locationError}</p>}

        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mt-2 mb-3">
          {QUICK_FILTERS.map((filter) => {
            const selected = (filter === 'Open now' && showOpenOnly) ||
              (filter === 'Nearest' && sortBy === 'distance') ||
              (filter === 'Top rated' && sortBy === 'rating');

            return (
              <button
                key={filter}
                onClick={() => {
                  if (filter === 'Open now') {
                    setShowOpenOnly((prev) => !prev);
                    return;
                  }
                  if (filter === 'Nearest') {
                    setSortBy('distance');
                    return;
                  }
                  if (filter === 'Top rated') {
                    setSortBy('rating');
                    return;
                  }
                  setSearchQuery(filter);
                }}
                className={`shrink-0 rounded-full px-3 py-2 text-xs font-700 transition-all border ${selected ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20' : 'bg-muted text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'}`}
              >
                {filter}
              </button>
            );
          })}
        </div>

        {!user ? (
          <div className="mb-4 rounded-3xl border border-primary/15 bg-primary/5 px-4 py-4 sm:px-5 sm:py-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">Browsing as guest</p>
                <p className="text-sm text-muted-foreground">You can explore chefs now, search people, or sign in to post, order, report, and block.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link href="/search" className="inline-flex h-10 items-center justify-center rounded-full border border-border bg-background px-4 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                  Search people
                </Link>
                <Link href="/login" className="inline-flex h-10 items-center justify-center rounded-full border border-border bg-background px-4 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                  Sign in
                </Link>
                <Link href="/signup" className="inline-flex h-10 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
                  Create account
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        <div className="relative mb-3 mt-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chefs, food trucks, dishes, or cuisines"
            className="w-full bg-muted rounded-xl pl-9 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/30 transition-all"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {filteredVendors.length > 0 && (
          <section className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-[15px] font-700 text-foreground">Popular near you</h2>
                <p className="text-[12px] text-muted-foreground">Popular local chefs and food trucks customers are checking out.</p>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 -mx-1 px-1">
              {filteredVendors.slice(0, 6).map((vendor) => <FeaturedVendorCard key={`featured-${vendor.id}`} vendor={vendor} />)}
            </div>
          </section>
        )}

        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 whitespace-nowrap transition-all duration-200 shrink-0 active:scale-95 ${selectedCategory === cat.id ? 'bg-primary text-white shadow-md shadow-primary/20 scale-105' : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground hover:scale-105 hover:shadow-sm'}`}
            >
              <span>{cat.emoji}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {!loadingVendors && filteredVendors.length > 0 && (
          <div className="flex items-center justify-between mb-3 mt-1">
            <div>
              <h2 className="text-[15px] font-700 text-foreground">Nearby chefs</h2>
              <p className="text-[12px] text-muted-foreground">Browse chefs, compare vibes, and order fast.</p>
            </div>
          </div>
        )}

        {loadingVendors ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-8">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="bg-card rounded-2xl overflow-hidden border border-border/50 animate-pulse">
                <div className="h-44 bg-muted" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-10 bg-muted rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredVendors.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-8">
            {filteredVendors.map((vendor) => <VendorCard key={vendor.id} vendor={vendor} />)}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <Truck className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-700 text-foreground mb-1">No chefs found nearby</h3>
            <p className="text-sm text-muted-foreground max-w-sm">Try updating your location or widening your radius from the location sheet. You can still browse popular chefs when they become available.</p>
          </div>
        )}
      </div>

      {showLocationSheet && (
        <div className="fixed inset-0 z-[70] bg-black/45 backdrop-blur-sm flex items-end sm:items-center sm:justify-center">
          <div className="w-full sm:max-w-lg bg-card rounded-t-3xl sm:rounded-3xl border border-border shadow-2xl p-4 sm:p-5 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-700 text-foreground">Addresses</h2>
              <button onClick={() => setShowLocationSheet(false)} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-border transition-colors">
                <X className="w-4 h-4 text-foreground" />
              </button>
            </div>

            <form onSubmit={handleCustomSubmit} className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  placeholder="Search an address"
                  className="w-full bg-muted rounded-2xl pl-10 pr-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/30"
                  autoFocus
                />
              </div>
            </form>

            <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide">
              <button type="button" onClick={requestBrowserLocation} className="shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-full bg-primary/10 hover:bg-primary/15 transition-colors text-sm font-700 text-primary">
                <LocateFixed className="w-4 h-4" /> Current location
              </button>
              <button type="button" onClick={() => profile?.location && handleLocationChange(profile.location)} className="shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-full bg-muted hover:bg-border transition-colors text-sm font-600 text-foreground">
                <Home className="w-4 h-4" /> Home
              </button>
              <button type="button" onClick={() => manualLocationLabel && handleLocationChange(manualLocationLabel)} className="shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-full bg-muted hover:bg-border transition-colors text-sm font-600 text-foreground">
                <Briefcase className="w-4 h-4" /> Work
              </button>
              <button type="button" onClick={() => customInput.trim() && handleLocationChange(customInput.trim())} className="shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-full bg-muted hover:bg-border transition-colors text-sm font-600 text-foreground">
                <Plus className="w-4 h-4" /> Use typed address
              </button>
            </div>

            <button onClick={requestBrowserLocation} className="w-full flex items-center gap-3 rounded-2xl px-1 py-3 text-left hover:bg-muted/40 transition-all mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <LocateFixed className="w-4.5 h-4.5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-700 text-foreground">Use current location</p>
                <p className="text-xs text-muted-foreground">Update with your live location</p>
              </div>
            </button>

            <div className="border-t border-border/60 my-3" />

            <div className="mb-4">
              <div className="rounded-2xl bg-muted/40 px-4 py-3 mb-3">
                <p className="text-xs font-700 text-muted-foreground uppercase tracking-wider mb-1">Current address</p>
                <p className="text-sm text-foreground font-600">{displayLocation.fullAddress}</p>
              </div>
              <p className="text-xs font-700 text-muted-foreground uppercase tracking-wider mb-2">Saved addresses</p>
              <div className="space-y-2">
                {savedAddresses.length > 0 ? savedAddresses.map((address) => {
                  const AddressIcon = address.icon === 'home' ? Home : Briefcase;
                  return (
                    <button key={address.id} onClick={() => handleLocationChange(address.value)} className="w-full flex items-center gap-3 rounded-2xl px-1 py-3 text-left hover:bg-muted/40 transition-all">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <AddressIcon className="w-4.5 h-4.5 text-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-700 text-foreground">{address.label}</p>
                        <p className="text-xs text-muted-foreground truncate">{address.value}</p>
                      </div>
                    </button>
                  );
                }) : (
                  <div className="rounded-2xl bg-muted/40 px-4 py-4 text-sm text-muted-foreground">
                    No saved addresses yet. Search above or use your current location.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl bg-muted/50 p-4 mt-2">
              <p className="text-xs font-700 text-muted-foreground uppercase tracking-wider mb-2">Advanced</p>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-700 text-foreground">Search radius</p>
                  <p className="text-xs text-muted-foreground">Only chefs within this distance will appear.</p>
                </div>
                <select value={customerRadiusMiles} onChange={(e) => setCustomerRadiusMiles(Number(e.target.value))} className="bg-card border border-border rounded-xl px-3 py-2 text-sm text-foreground outline-none">
                  {CUSTOMER_RADIUS_OPTIONS.map((radius) => <option key={radius} value={radius}>{radius} mi</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
